import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSearchField,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminSeverityText,
  AdminSupportingText,
  AdminAlertText,
  AdminMetric,
  AdminMetricDelta,
  AdminListFilterDropdown,
  toAdminListFilterOptions,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
  ADMIN_SECTION_OFFSET_CLASS,
  ADMIN_METRIC_PANEL_CLASS,
} from '../shared/adminUx';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';

const PAGE_SIZE = 25;

const REPORT_TYPE_OPTIONS = ['All', 'Customer Report', 'Business Report', 'Misconduct Report', 'Fraud/Identity Concern', 'Harassment/Abuse Report', 'Safety Concern', 'Licence Re-check', 'Document Clarification', 'Admin Flag (Manual)', 'Other Manual Trigger'];
const SEVERITY_OPTIONS = ['All', 'Level 1 — Low', 'Level 2 — Medium', 'Level 3 — High', 'Level 4 — Critical'];
const CASE_STATUS_OPTIONS = ['All', 'New', 'Assigned', 'In Review', 'Escalated', 'Action Applied', 'Closed'];
const REPORTER_TYPE_OPTIONS = ['All', 'Customer', 'Business', 'Admin', 'System-Assisted (non-auto flag)'];
const ENTITY_TYPE_OPTIONS = ['All', 'Post', 'Review', 'Message', 'Business Profile', 'Customer Profile', 'Document', 'Licence', 'Misconduct'];
const DATE_RANGE_OPTIONS = ['All', 'Last 7 days', 'Last 30 days', 'Last 90 days', 'Custom'];
const REGION_OPTIONS = ['All', 'NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'];

const KPI_DEFAULT = {
  totalManualCases: { value: 0, change: 0 },
  newReports24h: { value: 0, change: 0 },
  pendingReview: { value: 0, change: 0 },
  pendingAssignment: { value: 0, change: 0 },
  highSeverity: { value: 0, change: 0 },
  misconductReports: { value: 0, change: 0 },
  identityFraudReports: { value: 0, change: 0 },
  licenceDocumentRechecks: { value: 0, change: 0 },
  escalationsRequired: { value: 0, change: 0 },
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatMetric = (value) => {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString();
};

const maskName = (name) => {
  if (!name) return '—';
  const parts = name.split(' ');
  if (parts.length > 1) {
    return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
  }
  return `${name.charAt(0)}${'*'.repeat(Math.max(2, name.length - 2))}`;
};

const generateManualReviewCases = () => {
  const reportTypes = ['Customer Report', 'Business Report', 'Misconduct Report', 'Fraud/Identity Concern', 'Harassment/Abuse Report', 'Safety Concern', 'Licence Re-check', 'Document Clarification', 'Admin Flag (Manual)', 'Other Manual Trigger'];
  const severities = ['Level 1 — Low', 'Level 2 — Medium', 'Level 3 — High', 'Level 4 — Critical'];
  const statuses = ['New', 'Assigned', 'In Review', 'Escalated', 'Action Applied', 'Closed'];
  const entityTypes = ['Post', 'Review', 'Message', 'Business Profile', 'Customer Profile', 'Document', 'Licence', 'Misconduct'];
  const reporterTypes = ['Customer', 'Business', 'Admin', 'System-Assisted (non-auto flag)'];
  const admins = ['John Smith', 'Sarah Johnson', 'Mike Davis', 'Emma Wilson', null];
  const customerNames = ['John Doe', 'Jane Smith', 'Bob Wilson', 'Alice Brown', 'Charlie Davis'];
  const businessNames = ['ABC Renovations', 'XYZ Plumbing', 'Best Electrical', 'Premium Builders', 'Top Carpentry'];

  const randomFrom = (array) => array[Math.floor(Math.random() * array.length)];
  const items = [];
  const now = new Date();

  for (let i = 1; i <= 200; i++) {
    const reportType = randomFrom(reportTypes);
    const severity = randomFrom(severities);
    const status = randomFrom(statuses);
    const entityType = randomFrom(entityTypes);
    const reporterType = randomFrom(reporterTypes);
    const assignedTo = status === 'New' ? null : randomFrom(admins);

    let entityId = '';
    if (entityType === 'Post') entityId = `POST#${String(i).padStart(4, '0')}`;
    else if (entityType === 'Review') entityId = `RV#${String(i).padStart(4, '0')}`;
    else if (entityType === 'Message') entityId = `MSG#${String(i).padStart(4, '0')}`;
    else if (entityType === 'Business Profile') entityId = `BP#${String(i).padStart(4, '0')}`;
    else if (entityType === 'Customer Profile') entityId = `CP#${String(i).padStart(4, '0')}`;
    else if (entityType === 'Document') entityId = `DOC#${String(i).padStart(4, '0')}`;
    else if (entityType === 'Licence') entityId = `DOC#${String(i).padStart(4, '0')}`;
    else entityId = `MC#${String(i).padStart(4, '0')}`;

    const dateSubmitted = new Date(now);
    dateSubmitted.setDate(dateSubmitted.getDate() - Math.floor(Math.random() * 90));
    const region = randomFrom(REGION_OPTIONS.filter((r) => r !== 'All'));

    let reporter = '';
    if (reporterType === 'Customer') {
      reporter = maskName(randomFrom(customerNames));
    } else if (reporterType === 'Business') {
      reporter = randomFrom(businessNames);
    } else if (reporterType === 'Admin') {
      reporter = `Admin#${String(i % 10).padStart(3, '0')}`;
    } else {
      reporter = 'System-Assisted';
    }

    items.push({
      id: `MC#${String(i).padStart(5, '0')}`,
      caseId: `MC#${String(i).padStart(5, '0')}`,
      reportType,
      entityType,
      entityId,
      reporter,
      reporterType,
      severity,
      status,
      assignedTo,
      dateSubmitted: dateSubmitted.toISOString(),
      region,
    });
  }

  return items;
};

const renderDelta = (change) => (
  <div className="mt-1">
    <AdminMetricDelta value={change} />
  </div>
);

const KPI_CARDS = [
  { key: 'totalManualCases', label: 'Total manual cases' },
  { key: 'newReports24h', label: 'New reports (last 24h)' },
  { key: 'pendingReview', label: 'Pending review' },
  { key: 'pendingAssignment', label: 'Pending assignment' },
  { key: 'highSeverity', label: 'High severity (L3–L4)' },
  { key: 'misconductReports', label: 'Misconduct reports' },
  { key: 'identityFraudReports', label: 'Identity & fraud reports' },
  { key: 'licenceDocumentRechecks', label: 'Licence/document re-checks' },
  { key: 'escalationsRequired', label: 'Escalations required' },
];

const TABLE_GRID_COLUMNS =
  'minmax(100px, 1fr) minmax(140px, 1.3fr) minmax(100px, 1fr) minmax(120px, 1.1fr) minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(100px, 1fr) minmax(120px, 1.1fr) minmax(140px, 1.3fr)';

const ManualReviewQueue = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [kpis, setKpis] = useState(KPI_DEFAULT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [filters, setFilters] = useState({
    reportType: 'All',
    severity: 'All',
    caseStatus: 'All',
    reporterType: 'All',
    assignedAdmin: 'All',
    entityType: 'All',
    dateRange: 'All',
    region: 'All',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('dateSubmitted');
  const [sortOrder, setSortOrder] = useState('desc');

  const [reportTypeOpen, setReportTypeOpen] = useState(false);
  const [severityOpen, setSeverityOpen] = useState(false);
  const [caseStatusOpen, setCaseStatusOpen] = useState(false);
  const [reporterTypeOpen, setReporterTypeOpen] = useState(false);
  const [assignedAdminOpen, setAssignedAdminOpen] = useState(false);
  const [entityTypeOpen, setEntityTypeOpen] = useState(false);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [regionOpen, setRegionOpen] = useState(false);

  const reportTypeRef = useRef(null);
  const severityRef = useRef(null);
  const caseStatusRef = useRef(null);
  const reporterTypeRef = useRef(null);
  const assignedAdminRef = useRef(null);
  const entityTypeRef = useRef(null);
  const dateRangeRef = useRef(null);
  const regionRef = useRef(null);

  useClickOutside(reportTypeRef, () => setReportTypeOpen(false));
  useClickOutside(severityRef, () => setSeverityOpen(false));
  useClickOutside(caseStatusRef, () => setCaseStatusOpen(false));
  useClickOutside(reporterTypeRef, () => setReporterTypeOpen(false));
  useClickOutside(assignedAdminRef, () => setAssignedAdminOpen(false));
  useClickOutside(entityTypeRef, () => setEntityTypeOpen(false));
  useClickOutside(dateRangeRef, () => setDateRangeOpen(false));
  useClickOutside(regionRef, () => setRegionOpen(false));

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      setError(null);
      try {
        await new Promise((resolve) => setTimeout(resolve, 350));
        
        const cases = generateManualReviewCases();
        setItems(cases);
        
        // Calculate KPIs
        const now = new Date();
        const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const last7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        const totalCases = cases.length;
        const newReports24h = cases.filter(c => new Date(c.dateSubmitted) >= last24h).length;
        const pendingReview = cases.filter(c => c.status === 'In Review').length;
        const pendingAssignment = cases.filter(c => c.status === 'New' || c.status === 'Assigned').length;
        const highSeverity = cases.filter(c => c.severity === 'Level 3 — High' || c.severity === 'Level 4 — Critical').length;
        const misconductReports = cases.filter(c => c.reportType === 'Misconduct Report').length;
        const identityFraudReports = cases.filter(c => c.reportType === 'Fraud/Identity Concern').length;
        const licenceDocumentRechecks = cases.filter(c => c.reportType === 'Licence Re-check' || c.reportType === 'Document Clarification').length;
        const escalationsRequired = cases.filter(c => c.status === 'Escalated').length;

        setKpis({
          totalManualCases: { value: totalCases, change: 5 },
          newReports24h: { value: newReports24h, change: 12 },
          pendingReview: { value: pendingReview, change: -8 },
          pendingAssignment: { value: pendingAssignment, change: -15 },
          highSeverity: { value: highSeverity, change: 3 },
          misconductReports: { value: misconductReports, change: 2 },
          identityFraudReports: { value: identityFraudReports, change: -5 },
          licenceDocumentRechecks: { value: licenceDocumentRechecks, change: 8 },
          escalationsRequired: { value: escalationsRequired, change: 1 },
        });
      } catch (err) {
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, []);

  // Get unique admins for filter
  const adminOptions = useMemo(() => {
    const admins = ['All', 'Unassigned'];
    const uniqueAdmins = new Set(items.filter(item => item.assignedTo).map(item => item.assignedTo));
    return [...admins, ...Array.from(uniqueAdmins).sort()];
  }, [items]);

  // Filter and search logic
  const filteredItems = useMemo(() => {
    let filtered = [...items];

    if (debouncedSearch) {
      const search = debouncedSearch.toLowerCase();
      filtered = filtered.filter(item =>
        item.caseId.toLowerCase().includes(search) ||
        item.reportType.toLowerCase().includes(search) ||
        item.entityType.toLowerCase().includes(search) ||
        item.entityId.toLowerCase().includes(search) ||
        item.reporter.toLowerCase().includes(search)
      );
    }

    if (filters.reportType !== 'All') {
      filtered = filtered.filter(item => item.reportType === filters.reportType);
    }

    if (filters.severity !== 'All') {
      filtered = filtered.filter(item => item.severity === filters.severity);
    }

    if (filters.caseStatus !== 'All') {
      filtered = filtered.filter(item => item.status === filters.caseStatus);
    }

    if (filters.reporterType !== 'All') {
      filtered = filtered.filter(item => item.reporterType === filters.reporterType);
    }

    if (filters.assignedAdmin !== 'All') {
      if (filters.assignedAdmin === 'Unassigned') {
        filtered = filtered.filter(item => !item.assignedTo);
      } else {
        filtered = filtered.filter(item => item.assignedTo === filters.assignedAdmin);
      }
    }

    if (filters.entityType !== 'All') {
      filtered = filtered.filter(item => item.entityType === filters.entityType);
    }

    if (filters.region !== 'All') {
      filtered = filtered.filter((item) => item.region === filters.region);
    }

    if (filters.dateRange !== 'All' && filters.dateRange !== 'Custom') {
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      const days =
        filters.dateRange === 'Last 7 days'
          ? 7
          : filters.dateRange === 'Last 30 days'
            ? 30
            : filters.dateRange === 'Last 90 days'
              ? 90
              : null;
      if (days) {
        const cutoff = new Date(now);
        cutoff.setDate(cutoff.getDate() - days);
        cutoff.setHours(0, 0, 0, 0);
        filtered = filtered.filter((item) => {
          const submitted = item.dateSubmitted ? new Date(item.dateSubmitted) : null;
          if (!submitted || Number.isNaN(submitted.getTime())) return false;
          return submitted >= cutoff && submitted <= now;
        });
      }
    }

    return filtered;
  }, [items, debouncedSearch, filters]);

  // Sort logic
  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'dateSubmitted') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
    return sorted;
  }, [filteredItems, sortField, sortOrder]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);

  const paginatedItems = useMemo(() => {
    const start = (effectivePage - 1) * PAGE_SIZE;
    return sortedItems.slice(start, start + PAGE_SIZE);
  }, [sortedItems, effectivePage]);

  const rangeStart = sortedItems.length === 0 ? 0 : (effectivePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * PAGE_SIZE, sortedItems.length);
  const showPagination = sortedItems.length > PAGE_SIZE;

  const reportTypeFilterOptions = useMemo(
    () => toAdminListFilterOptions(REPORT_TYPE_OPTIONS, 'All'),
    [],
  );
  const severityFilterOptions = useMemo(
    () => toAdminListFilterOptions(SEVERITY_OPTIONS, 'All'),
    [],
  );
  const caseStatusFilterOptions = useMemo(
    () => toAdminListFilterOptions(CASE_STATUS_OPTIONS, 'All'),
    [],
  );
  const reporterTypeFilterOptions = useMemo(
    () => toAdminListFilterOptions(REPORTER_TYPE_OPTIONS, 'All'),
    [],
  );
  const assignedAdminFilterOptions = useMemo(
    () => toAdminListFilterOptions(adminOptions, 'All'),
    [adminOptions],
  );
  const entityTypeFilterOptions = useMemo(
    () => toAdminListFilterOptions(ENTITY_TYPE_OPTIONS, 'All'),
    [],
  );
  const dateRangeFilterOptions = useMemo(
    () => toAdminListFilterOptions(DATE_RANGE_OPTIONS, 'All'),
    [],
  );
  const regionFilterOptions = useMemo(
    () => toAdminListFilterOptions(REGION_OPTIONS, 'All'),
    [],
  );

  const renderHeader = () => (
    <div
      className="grid py-3 text-offblack border-b border-lightgray px-4 gap-3"
      style={{ gridTemplateColumns: TABLE_GRID_COLUMNS, width: '100%' }}
    >
      {['Case ID', 'Report type', 'Entity type', 'Entity ID', 'Reporter', 'Severity', 'Status', 'Assigned admin', 'Date submitted'].map((label) => (
        <div className="flex items-center" key={label}>
          <AdminTableHeader>{label}</AdminTableHeader>
        </div>
      ))}
    </div>
  );

  const renderRow = (item) => (
    <div
      key={item.id}
      onClick={() => navigate(`/admin/moderation/manual/${item.caseId.replace('MC#', '')}`, { state: { from: location.pathname, item } })}
      className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-3 cursor-pointer"
      style={{ gridTemplateColumns: TABLE_GRID_COLUMNS, width: '100%' }}
    >
      <div className="flex items-center"><AdminTableBody>{item.caseId}</AdminTableBody></div>
      <div className="flex items-center min-w-0">
        <AdminTableBody className="truncate" title={item.reportType}>{item.reportType}</AdminTableBody>
      </div>
      <div className="flex items-center"><AdminTableBody>{item.entityType}</AdminTableBody></div>
      <div className="flex items-center"><AdminTableBody>{item.entityId}</AdminTableBody></div>
      <div className="flex items-center"><AdminTableBody>{item.reporter}</AdminTableBody></div>
      <div className="flex items-center">
        <AdminSeverityText severity={item.severity}>{item.severity.replace('Level ', 'L')}</AdminSeverityText>
      </div>
      <div className="flex items-center">
        <AdminStatusBadge status={item.status} label={item.status} />
      </div>
      <div className="flex items-center">
        <AdminTableBody>{item.assignedTo || 'Unassigned'}</AdminTableBody>
      </div>
      <div className="flex items-center"><AdminTableMeta>{formatDate(item.dateSubmitted)}</AdminTableMeta></div>
    </div>
  );

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Manual Review Queue</AdminPageTitle>

        <div className={`grid grid-cols-2 lg:grid-cols-5 gap-4 ${ADMIN_SECTION_OFFSET_CLASS}`}>
          {KPI_CARDS.map(({ key, label }) => (
            <DashboardMainContainer key={key} className={ADMIN_METRIC_PANEL_CLASS}>
              <AdminSupportingText className="text-offblack mb-2">{label}</AdminSupportingText>
              <AdminMetric size="large" className="text-offblack mb-2 block">
                {formatMetric(kpis[key]?.value)}
              </AdminMetric>
              {renderDelta(kpis[key]?.change)}
            </DashboardMainContainer>
          ))}
        </div>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full lg:max-w-[26.25rem]"
              type="text"
              placeholder="Search Case ID, Report Type, Entity ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search manual review cases"
            />

            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Report Type"
                value={filters.reportType}
                options={reportTypeFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, reportType: value }))}
                menuRef={reportTypeRef}
                open={reportTypeOpen}
                setOpen={setReportTypeOpen}
              />
              <AdminListFilterDropdown
                label="Severity"
                value={filters.severity}
                options={severityFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, severity: value }))}
                menuRef={severityRef}
                open={severityOpen}
                setOpen={setSeverityOpen}
              />
              <AdminListFilterDropdown
                label="Status"
                value={filters.caseStatus}
                options={caseStatusFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, caseStatus: value }))}
                menuRef={caseStatusRef}
                open={caseStatusOpen}
                setOpen={setCaseStatusOpen}
              />
              <AdminListFilterDropdown
                label="Reporter"
                value={filters.reporterType}
                options={reporterTypeFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, reporterType: value }))}
                menuRef={reporterTypeRef}
                open={reporterTypeOpen}
                setOpen={setReporterTypeOpen}
              />
              <AdminListFilterDropdown
                label="Assigned"
                value={filters.assignedAdmin}
                options={assignedAdminFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, assignedAdmin: value }))}
                menuRef={assignedAdminRef}
                open={assignedAdminOpen}
                setOpen={setAssignedAdminOpen}
              />
              <AdminListFilterDropdown
                label="Entity Type"
                value={filters.entityType}
                options={entityTypeFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, entityType: value }))}
                menuRef={entityTypeRef}
                open={entityTypeOpen}
                setOpen={setEntityTypeOpen}
              />
              <AdminListFilterDropdown
                label="Date Range"
                value={filters.dateRange}
                options={dateRangeFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, dateRange: value }))}
                menuRef={dateRangeRef}
                open={dateRangeOpen}
                setOpen={setDateRangeOpen}
              />
              <AdminListFilterDropdown
                label="Region"
                value={filters.region}
                options={regionFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, region: value }))}
                menuRef={regionRef}
                open={regionOpen}
                setOpen={setRegionOpen}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {loading ? (
            <div className="py-10 text-center">
              <AdminSupportingText className="text-mediumgray">Loading manual review queue...</AdminSupportingText>
            </div>
          ) : error ? (
            <div className="py-10 text-center">
              <AdminAlertText className="text-alertred block">{error}</AdminAlertText>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="py-10 text-center">
              <AdminSupportingText className="text-mediumgray">No cases found matching your filters.</AdminSupportingText>
            </div>
          ) : (
            <div className="overflow-hidden">
              {renderHeader()}
              {paginatedItems.map((item) => renderRow(item))}
            </div>
          )}
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={!loading && !error && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={sortedItems.length}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default ManualReviewQueue;

